"use client";

import React, { useEffect, useState } from "react";
import { Facebook, Instagram, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="74150" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-16 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex items-center justify-center bg-transparent px-1 py-2 text-[14px] font-medium uppercase tracking-wider text-foreground transition-colors hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-transparent data-[state=open]:bg-transparent relative after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-lg border-b border-primary/20 p-6 flex flex-col gap-6 md:hidden shadow-2xl animate-in slide-in-from-top-2">
      <nav className="flex flex-col gap-4">
        {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} onClick={onClose} className="text-lg font-medium uppercase tracking-wider text-foreground py-2 border-b border-border/30 hover:text-primary transition-colors">
            {item.label}
          </Link>)}
      </nav>
      <div className="flex items-center gap-6 pt-4">
        <Link to="#" className="text-primary hover:text-foreground transition-transform duration-300 hover:scale-110">
          <Facebook className="w-6 h-6" />
          <span className="sr-only">Facebook</span>
        </Link>
        <Link to="https://www.instagram.com/hekappers/" newTab className="text-primary hover:text-foreground transition-transform duration-300 hover:scale-110">
          <Instagram className="w-6 h-6" />
          <span className="sr-only">Instagram</span>
        </Link>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={cn("dark bg-background text-foreground sticky top-0 z-50 w-full transition-all duration-300", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-primary/40 shadow-lg" : "bg-background")}>
      <MenuProvider menu_id="16">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          
          <div className="flex-shrink-0">
            <Link to="/" className="inline-block transition-opacity hover:opacity-90">
              <div className="bg-black h-16 w-16 flex flex-col items-center justify-center rounded-lg border border-white/10">
                <span className="text-white font-serif text-xl font-bold leading-none tracking-tighter">H&E</span>
                <div className="h-[2px] w-10 bg-[#E11D48] my-1" />
                <span className="text-white font-serif text-[10px] font-medium uppercase leading-none tracking-widest">Kappers</span>
              </div>
            </Link>
          </div>

          <div className="flex-1 flex justify-center">
            <DesktopMenuConsumer />
          </div>

          <div className="flex items-center gap-6 flex-shrink-0">
            <div className="hidden md:flex items-center gap-5">
              <Link to="#" className="text-primary/80 hover:text-primary transition-transform duration-300 hover:scale-110">
                <Facebook className="w-5 h-5" />
                <span className="sr-only">Facebook</span>
              </Link>
              <Link to="https://www.instagram.com/hekappers/" newTab className="text-primary/80 hover:text-primary transition-transform duration-300 hover:scale-110">
                <Instagram className="w-5 h-5" />
                <span className="sr-only">Instagram</span>
              </Link>
            </div>

            <Button variant="ghost" size="icon" className="md:hidden text-primary hover:bg-primary/10" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Menu schakelen">
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        <MobileMenuConsumer isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}